import {Anchor, Group, Paper, Stack, Table, Text, Title} from '@mantine/core'
import {Link} from '@tanstack/react-router'

import {toLifecycleStatus} from 'src/jobs/job-api-adapter'
import {formatJobDate} from 'src/jobs/job-schedule-format'
import {JobStatusBadge} from 'src/jobs/job-status-badge'
import type {ProjectDetail} from 'src/projects/schemas'
import {PartialPickupIcon} from 'src/work/partial-pickup'

import {orderLabel} from './order-label'

type OrderJobsCardProps = {
    order: ProjectDetail
    currentJobId: string
}

/** Project jobs sorted by display id. The current job renders without a link. */
export function OrderJobsCard({order, currentJobId}: OrderJobsCardProps) {
    const jobs = [...order.jobs].sort((left, right) =>
        left.displayId.localeCompare(right.displayId),
    )
    return (
        <Paper withBorder p="md" radius="md" data-testid="order-jobs">
            <Stack gap="xs">
                <Group gap="xs">
                    <Title order={4}>
                        <Anchor
                            renderRoot={(props) => (
                                <Link
                                    {...props}
                                    to="/projects/$projectId"
                                    params={{projectId: order.id}}
                                />
                            )}
                            inherit
                        >
                            Order {orderLabel(order)}
                        </Anchor>
                    </Title>
                    <Text c="dimmed">
                        · {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
                    </Text>
                </Group>
                <Table>
                    <Table.Tbody>
                        {jobs.map((job) => (
                            <Table.Tr key={job.id} fw={job.id === currentJobId ? 600 : undefined}>
                                <Table.Td ff="monospace">
                                    {job.id === currentJobId ? (
                                        <Text span inherit>
                                            {job.displayId}
                                        </Text>
                                    ) : (
                                        <Anchor
                                            size="sm"
                                            ff="monospace"
                                            renderRoot={(props) => (
                                                <Link
                                                    {...props}
                                                    to="/jobs/$jobId"
                                                    params={{jobId: job.id}}
                                                />
                                            )}
                                        >
                                            {job.displayId}
                                        </Anchor>
                                    )}
                                </Table.Td>
                                <Table.Td>
                                    <Group gap={4} wrap="nowrap">
                                        <Text size="sm" inherit>
                                            {job.name}
                                            {job.id === currentJobId ? ' (this job)' : ''}
                                        </Text>
                                        <PartialPickupIcon partialPickup={job.partialPickup} />
                                    </Group>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm">
                                        {job.dateTbc ? 'date TBC' : formatJobDate(job.startDate)}
                                    </Text>
                                </Table.Td>
                                <Table.Td>
                                    <JobStatusBadge status={toLifecycleStatus(job.status)} />
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Stack>
        </Paper>
    )
}
